import React, { useMemo, useState } from 'react'
import api from '../../api/axios'
import { GROUPS } from '../../content/defaults'
import { T, useContent } from '../../content/ContentProvider'

/**
 * Administration → Portal content.
 *
 * Lists every text in the catalogue grouped by screen, shows which ones have
 * been overridden, and lets an administrator change, reset or inspect them.
 * After a successful write the provider is refreshed so the editor sees the
 * effect immediately, the same as any reader would after a refresh.
 */
export function PortalContent() {
  const { t, resolve, defaultFor, isOverridden, refresh } = useContent()
  const [drafts, setDrafts] = useState({})
  const [busy, setBusy] = useState(null)
  const [flash, setFlash] = useState(null)
  const [query, setQuery] = useState('')
  const [historyFor, setHistoryFor] = useState(null)
  const [historyRows, setHistoryRows] = useState([])

  const needle = query.trim().toLowerCase()
  const groups = useMemo(() => GROUPS.map((g) => {
    const keys = Object.keys(g.texts).filter((k) =>
      !needle || k.toLowerCase().includes(needle)
      || String(resolve(k) ?? '').toLowerCase().includes(needle))
    return { ...g, keys }
  }).filter((g) => g.keys.length > 0), [needle, resolve])

  const currentText = (key) =>
    Object.prototype.hasOwnProperty.call(drafts, key) ? drafts[key] : (resolve(key) ?? '')

  const isDirty = (key) =>
    Object.prototype.hasOwnProperty.call(drafts, key) && drafts[key] !== (resolve(key) ?? '')

  const clearDraft = (key) => setDrafts((d) => {
    const next = { ...d }
    delete next[key]
    return next
  })

  async function save(key) {
    // A default may deliberately start or end with a space because the code
    // joins it to a value. Trimming that away in the textarea would glue words
    // together, so the surrounding whitespace of the default is reapplied.
    const base = defaultFor(key) ?? ''
    const lead = base.match(/^\s*/)[0]
    const trail = base.match(/\s*$/)[0]
    const text = lead + String(currentText(key)).trim() + trail

    setBusy(key)
    try {
      await api.put(`/api/v1/content/${key}`, { text })
      await refresh()
      clearDraft(key)
      setFlash({ key, message: t('portalContent.saved') })
    } catch (e) {
      const detail = e?.response?.data?.error || e?.message || ''
      setFlash({ key, message: t('portalContent.saveFailed', { error: detail }), error: true })
    } finally {
      setBusy(null)
    }
  }

  async function reset(key) {
    setBusy(key)
    try {
      await api.delete(`/api/v1/content/${key}`)
      await refresh()
      clearDraft(key)
      setFlash({ key, message: t('portalContent.resetDone') })
    } catch (e) {
      const detail = e?.response?.data?.error || e?.message || ''
      setFlash({ key, message: t('portalContent.saveFailed', { error: detail }), error: true })
    } finally {
      setBusy(null)
    }
  }

  async function openHistory(key) {
    setHistoryFor(key)
    setHistoryRows([])
    try {
      const { data } = await api.get('/api/v1/content/history', { params: { key } })
      setHistoryRows(Array.isArray(data) ? data : [])
    } catch {
      setHistoryRows([])
    }
  }

  return (
    <div className="view">
      <div className="view__head">
        <div className="page-head">
          <div>
            <h1 className="page-head__h">{t('portalContent.title')}</h1>
            <div className="page-head__sub"><T k="portalContent.intro" /></div>
          </div>
        </div>
      </div>

      <div className="view__body">
        <div className="fld" style={{ maxWidth: 420, marginBottom: 'var(--sp-6)' }}>
          <label className="fld__label" htmlFor="content-search">
            {t('portalContent.search.label')}
          </label>
          <input
            id="content-search"
            className="fld__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('portalContent.search.placeholder')}
          />
        </div>

        {/* Written from keys plus literal spans: routing this hint through <T>
            would render the bold and the link instead of showing the syntax. */}
        <p className="muted" style={{ marginBottom: 'var(--sp-6)' }}>
          {t('portalContent.hint.lead')} <span className="mono">{'{name}'}</span>{' '}
          {t('portalContent.hint.placeholders')} <span className="mono">**bold**</span>,{' '}
          <span className="mono">[label](https://…)</span>{' '}
          {t('portalContent.hint.markdown')}
        </p>

        {groups.length === 0 && <p className="muted">{t('portalContent.empty')}</p>}

        {groups.map((group) => {
          const changed = group.keys.filter(isOverridden).length
          return (
            <details
              key={group.id}
              className="card"
              style={{ marginBottom: 'var(--sp-4)' }}
              /* Open while searching: otherwise the groups narrow down but stay
                 collapsed, so a search that matched looks like one that failed. */
              open={needle ? true : undefined}
            >
              <summary style={{ cursor: 'pointer', padding: 'var(--sp-4)' }}>
                <strong>{group.title}</strong>{' '}
                <span className="muted">
                  {t('portalContent.groupCount', { changed, total: group.keys.length })}
                </span>
              </summary>

              <div style={{ padding: 'var(--sp-4)' }}>
                {group.note && <p className="muted">{group.note}</p>}

                {group.keys.map((key) => (
                  <div key={key} style={{ marginBottom: 'var(--sp-6)' }}>
                    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
                      <code className="mono">{key}</code>
                      {isOverridden(key) && (
                        <span className="badge">{t('portalContent.changedBadge')}</span>
                      )}
                    </div>

                    <textarea
                      className="fld__input"
                      rows={2}
                      aria-label={key}
                      value={currentText(key)}
                      onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                    />

                    {isOverridden(key) && (
                      <div className="muted" style={{ fontSize: 'var(--fs-label)' }}>
                        {t('portalContent.defaultLabel')} {defaultFor(key)}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
                      <button
                        type="button"
                        className="btn btn--primary btn--sm"
                        disabled={!isDirty(key) || busy === key}
                        onClick={() => save(key)}
                      >
                        {t('portalContent.action.save')}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!isDirty(key) || busy === key}
                        onClick={() => clearDraft(key)}
                      >
                        {t('portalContent.action.discard')}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!isOverridden(key) || busy === key}
                        onClick={() => reset(key)}
                      >
                        {t('portalContent.action.reset')}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => openHistory(key)}
                      >
                        {t('portalContent.action.history')}
                      </button>
                    </div>

                    {flash?.key === key && (
                      <div
                        className={`status-msg ${flash.error ? 'status-msg--err' : 'status-msg--ok'}`}
                        role={flash.error ? 'alert' : 'status'}
                        aria-live="polite"
                      >
                        {flash.message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )
        })}

        {historyFor && (
          <div className="card" style={{ padding: 'var(--sp-4)' }}>
            <h2>{t('portalContent.history.title')}</h2>
            <code className="mono">{historyFor}</code>
            {historyRows.length === 0 && (
              <p className="muted">{t('portalContent.history.empty')}</p>
            )}
            <ul>
              {historyRows.map((h, i) => (
                <li key={i}>
                  <span className="muted">{h.changedAt} · {h.changedBy}</span>
                  {' — '}
                  {h.wasDefault
                    ? t('portalContent.history.wasDefault')
                    : <q>{h.oldText}</q>}
                  {' → '}
                  {h.resetToDefault
                    ? t('portalContent.history.resetToDefault')
                    : <q>{h.newText}</q>}
                </li>
              ))}
            </ul>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setHistoryFor(null)}>
              {t('portalContent.history.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PortalContent
